import type { TrackingShare, TrackingShareScope } from '@/lib/api/types'

export type TrackingShareState = 'active' | 'expired' | 'revoked'
export type PublicTrackingSection = keyof TrackingShareScope

export interface PublicTrackingFragment {
  token: string | null
  documentIds: string[]
  sections: PublicTrackingSection[]
}

export interface PublicTrackingSessionConfig {
  documentIds: string[]
  sections: PublicTrackingSection[]
}

const consumedFragments = new Map<string, PublicTrackingFragment>()
const validSections = new Set<PublicTrackingSection>([
  'summary',
  'timeline',
  'documents',
  'live_position',
  'containers',
])

function unique(values: string[]) {
  return Array.from(new Set(values.filter(Boolean)))
}

export function parsePublicTrackingFragment(hash: string): PublicTrackingFragment {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const documentIds = unique(
    (params.get('documents') ?? '').split(',').map((value) => value.trim()),
  )
  const sections = unique(
    (params.get('sections') ?? '').split(',').map((value) => value.trim()),
  ).filter((value): value is PublicTrackingSection =>
    validSections.has(value as PublicTrackingSection),
  )

  return {
    token: params.get('token')?.trim() || null,
    documentIds,
    sections,
  }
}

export function consumePublicTrackingFragment(
  publicId: string,
  location: Pick<Location, 'hash' | 'pathname' | 'search'> = window.location,
  browserHistory: Pick<History, 'replaceState' | 'state'> = window.history,
): PublicTrackingFragment {
  const cached = consumedFragments.get(publicId)
  if (cached) return cached

  const fragment = parsePublicTrackingFragment(location.hash)
  if (location.hash) {
    browserHistory.replaceState(browserHistory.state, '', `${location.pathname}${location.search}`)
  }
  if (fragment.token || fragment.documentIds.length || fragment.sections.length) {
    consumedFragments.set(publicId, fragment)
  }
  return fragment
}

export function clearConsumedPublicTrackingFragment(publicId: string) {
  consumedFragments.delete(publicId)
}

export function buildPublicTrackingShareUrl(
  shareUrl: string,
  share: Pick<TrackingShare, 'document_ids' | 'scope'>,
  baseUrl = window.location.origin,
) {
  const url = new URL(shareUrl, baseUrl)
  const params = new URLSearchParams(url.hash.startsWith('#') ? url.hash.slice(1) : url.hash)
  if (share.document_ids.length) params.set('documents', share.document_ids.join(','))
  const sections = (Object.keys(share.scope) as PublicTrackingSection[]).filter(
    (section) => share.scope[section],
  )
  if (sections.length) params.set('sections', sections.join(','))
  url.hash = params.toString()
  return url.toString()
}

export function getTrackingShareState(share: TrackingShare, now = new Date()): TrackingShareState {
  if (share.revoked_at) return 'revoked'
  if (
    new Date(share.expires_at).getTime() <= now.getTime() ||
    share.view_count >= share.max_views
  ) {
    return 'expired'
  }
  return 'active'
}

function storageKey(publicId: string) {
  return `helm-public-tracking:${publicId}`
}

export function savePublicTrackingSessionConfig(
  publicId: string,
  config: PublicTrackingSessionConfig,
  storage: Pick<Storage, 'setItem'> = window.sessionStorage,
) {
  storage.setItem(storageKey(publicId), JSON.stringify(config))
}

export function loadPublicTrackingSessionConfig(
  publicId: string,
  storage: Pick<Storage, 'getItem'> = window.sessionStorage,
): PublicTrackingSessionConfig {
  try {
    const value = storage.getItem(storageKey(publicId))
    if (!value) return { documentIds: [], sections: [] }
    const parsed = JSON.parse(value) as Partial<PublicTrackingSessionConfig>
    return {
      documentIds: unique(Array.isArray(parsed.documentIds) ? parsed.documentIds : []),
      sections: unique(Array.isArray(parsed.sections) ? parsed.sections : []).filter(
        (section): section is PublicTrackingSection =>
          validSections.has(section as PublicTrackingSection),
      ),
    }
  } catch {
    return { documentIds: [], sections: [] }
  }
}

export function clearPublicTrackingSessionConfig(
  publicId: string,
  storage: Pick<Storage, 'removeItem'> = window.sessionStorage,
) {
  storage.removeItem(storageKey(publicId))
}
